import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { useToast } from '../components/ui/toast'
import { getFeatureFlags, updateFeatureFlag, type FeatureFlag } from '../lib/api/featureFlags'

function formatDate(value?: string | null) {
  if (!value) return 'Never'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value))
}

export const FeatureFlagsPage = () => {
  const { addToast } = useToast()
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  const groupedFlags = useMemo(() => {
    return flags.reduce<Record<string, FeatureFlag[]>>((groups, flag) => {
      const category = flag.category || 'General'
      groups[category] = groups[category] ?? []
      groups[category].push(flag)
      return groups
    }, {})
  }, [flags])

  const hydrate = useCallback(async () => {
    setLoading(true)
    try {
      setFlags(await getFeatureFlags())
    } catch (error) {
      console.error('Failed to load feature flags', error)
      addToast({
        title: 'Could not load feature flags',
        description: 'Please refresh or try again later.',
        variant: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    void hydrate()
  }, [hydrate])

  const handleToggle = async (flag: FeatureFlag) => {
    setSavingKey(flag.key)
    try {
      const updated = await updateFeatureFlag(flag.key, { enabled: !flag.enabled })
      setFlags((current) => current.map((item) => item.key === updated.key ? updated : item))
      addToast({
        title: `${updated.name} ${updated.enabled ? 'enabled' : 'disabled'}`,
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to update feature flag', error)
      addToast({
        title: 'Could not update feature flag',
        description: 'The flag was not changed.',
        variant: 'error'
      })
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Feature Flags</h1>
          <p className="mt-1 text-sm text-muted-foreground">Runtime switches for operational rollout and testing.</p>
        </div>
        <Button variant="outline" onClick={hydrate} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading feature flags
          </CardContent>
        </Card>
      ) : flags.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            No feature flags are registered.
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedFlags).map(([category, categoryFlags]) => (
          <section key={category} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{category}</h2>
            <div className="grid gap-3">
              {categoryFlags.map((flag) => {
                const saving = savingKey === flag.key
                const Icon = flag.enabled ? ToggleRight : ToggleLeft

                return (
                  <Card key={flag.key}>
                    <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-base">{flag.name}</CardTitle>
                        <CardDescription>{flag.description}</CardDescription>
                      </div>
                      <Button
                        variant={flag.enabled ? 'default' : 'outline'}
                        onClick={() => handleToggle(flag)}
                        disabled={saving}
                        className="w-full sm:w-auto"
                      >
                        {saving ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Icon className="mr-2 h-4 w-4" />
                        )}
                        {flag.enabled ? 'Enabled' : 'Disabled'}
                      </Button>
                    </CardHeader>
                    <CardContent className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
                      <div>
                        <div className="font-medium text-foreground">Key</div>
                        <div className="mt-1 font-mono text-xs">{flag.key}</div>
                      </div>
                      <div>
                        <div className="font-medium text-foreground">Default</div>
                        <div className="mt-1">{flag.defaultEnabled ? 'Enabled' : 'Disabled'}</div>
                      </div>
                      <div>
                        <div className="font-medium text-foreground">Last Updated</div>
                        <div className="mt-1">{formatDate(flag.updatedAt)}</div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </section>
        ))
      )}
    </div>
  )
}

